import { zodResolver } from '@hookform/resolvers/zod';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { Controller, useForm, useWatch } from 'react-hook-form';

import type { ArcStandardSection } from '@/api/generated';
import { Field } from '@/components/ui/Field';

import { newSectionSchema, type NewSectionValues } from './arcSchemas';
import { STANDARD_SECTIONS, type ArcSection } from './arcTypes';
import { STANDARD_NAMES } from './draftEdits';
import { FormDialog } from './FormDialog';

interface NewSectionDialogProps {
  sections: ArcSection[];
  /** Adds the section to the working copy; Save Draft saves it. */
  onApply: (kind: 'custom' | ArcStandardSection, name: string) => void;
  onClose: () => void;
}

/**
 * Adds a section: a custom one, which can hold any type of content, or a
 * standard one that was deleted, added back (E.2: every section can be
 * deleted, so the Builder must offer the way back). A standard section
 * added back takes its usual name unless one is typed.
 */
export function NewSectionDialog({ sections, onApply, onClose }: NewSectionDialogProps) {
  const missing = STANDARD_SECTIONS.filter(
    (standard) => !sections.some((section) => section.standard_type === standard.type),
  );

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<NewSectionValues>({
    resolver: zodResolver(newSectionSchema),
    defaultValues: { kind: 'custom', name: '' },
  });
  const kind = useWatch({ control, name: 'kind' });

  const submit = handleSubmit(({ kind: chosen, name }) => {
    onApply(chosen === 'custom' ? 'custom' : (chosen as ArcStandardSection), name);
    onClose();
  });

  return (
    <FormDialog
      open
      title="New section"
      description="A custom section can hold any type of content. A standard section that was deleted can be added back."
      dirty={isDirty}
      pending={false}
      failure={null}
      submitLabel="Add section"
      onSubmit={(event) => void submit(event)}
      onClose={onClose}
      testId="arc-builder-new-section-dialog"
    >
      {missing.length > 0 ? (
        <Controller
          name="kind"
          control={control}
          render={({ field }) => (
            <Field
              label="What to add"
              error={errors.kind?.message}
              testId="arc-builder-new-section-dialog-kind"
            >
              <TextField select {...field} size="small" fullWidth>
                <MenuItem value="custom">Custom section</MenuItem>
                {missing.map((standard) => (
                  <MenuItem key={standard.type} value={standard.type}>
                    Add back: {STANDARD_NAMES[standard.type] ?? standard.type}
                  </MenuItem>
                ))}
              </TextField>
            </Field>
          )}
        />
      ) : null}
      <Field
        label="Section name"
        hint={
          kind === 'custom'
            ? 'Up to 80 characters'
            : `Leave empty to use “${STANDARD_NAMES[kind] ?? kind}”`
        }
        error={errors.name?.message}
        testId="arc-builder-new-section-dialog-name"
      >
        <TextField {...register('name')} size="small" fullWidth />
      </Field>
    </FormDialog>
  );
}
